import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend, LineChart, Line, CartesianGrid,
} from "recharts";

const BASE = process.env.REACT_APP_API_BASE || "http://localhost/twinpack/php";

const COLORS = ["#9f0059","#c94090","#e07ab0","#f5b8d8","#6366F1","#F97316","#22C55E","#0EA5E9","#F59E0B","#EF4444"];

const formatFecha = (str) => {
  if (!str || str === "0000-00-00") return "—";
  return new Date(str).toLocaleDateString("es-AR", { day: "2-digit", month: "2-digit", year: "numeric" });
};

const formatMes = (str) => {
  if (!str) return "";
  const [y, m] = str.split("-");
  const meses = ["Ene","Feb","Mar","Abr","May","Jun","Jul","Ago","Sep","Oct","Nov","Dic"];
  return `${meses[parseInt(m) - 1]} ${y}`;
};

// ─── Estilos ──────────────────────────────────────────────────────────────────
const S = {
  page: {
    minHeight: "100vh", background: "#f3f0f4",
    padding: "28px 20px", fontFamily: "'Segoe UI', system-ui, sans-serif",
  },
  header: {
    maxWidth: 1200, margin: "0 auto 24px",
    display: "flex", alignItems: "flex-end",
    justifyContent: "space-between", flexWrap: "wrap", gap: 12,
  },
  title: { fontSize: 22, fontWeight: 700, color: "#121212", margin: 0 },
  subtitle: { fontSize: 13, color: "#888", marginTop: 2 },
  select: {
    padding: "8px 14px", border: "1.5px solid #e0d6dc",
    borderRadius: 8, fontSize: 13, color: "#333",
    background: "#fff", outline: "none", cursor: "pointer",
  },
  // ── KPI cards ──
  kpiRow: {
    maxWidth: 1200, margin: "0 auto 24px",
    display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 14,
  },
  kpiCard: {
    background: "#fff", borderRadius: 14, padding: "18px 20px",
    boxShadow: "0 1px 8px rgba(0,0,0,0.06)",
    borderLeft: "4px solid #9f0059",
  },
  kpiLabel: { fontSize: 11, fontWeight: 700, color: "#aaa", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 6 },
  kpiValue: { fontSize: 26, fontWeight: 800, color: "#121212" },
  kpiSub: { fontSize: 12, color: "#888", marginTop: 2 },
  // ── Gráficos ──
  chartsRow: {
    maxWidth: 1200, margin: "0 auto 24px",
    display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16,
  },
  chartCard: {
    background: "#fff", borderRadius: 14, padding: "20px",
    boxShadow: "0 1px 8px rgba(0,0,0,0.06)",
  },
  chartTitle: { fontSize: 13, fontWeight: 700, color: "#9f0059", marginBottom: 16, textTransform: "uppercase", letterSpacing: "0.06em" },
  chartCardFull: {
    maxWidth: 1200, margin: "0 auto 24px",
    background: "#fff", borderRadius: 14, padding: "20px",
    boxShadow: "0 1px 8px rgba(0,0,0,0.06)",
  },
  // ── Tabla ──
  tableWrap: {
    maxWidth: 1200, margin: "0 auto",
    background: "#fff", borderRadius: 14, overflow: "hidden",
    boxShadow: "0 1px 8px rgba(0,0,0,0.06)",
  },
  th: {
    background: "#9f0059", color: "#fff",
    padding: "12px 16px", fontSize: 11, fontWeight: 700,
    letterSpacing: "0.08em", textTransform: "uppercase", textAlign: "left",
    whiteSpace: "nowrap",
  },
  td: {
    padding: "12px 16px", fontSize: 13, color: "#222",
    borderBottom: "1px solid #f5f0f3", verticalAlign: "middle",
  },
  diasChip: (dias) => ({
    display: "inline-block", padding: "3px 10px", borderRadius: 20,
    fontSize: 12, fontWeight: 700,
    background: dias <= 14 ? "#d4f5e2" : dias <= 30 ? "#fff3cd" : "#fde8e8",
    color: dias <= 14 ? "#0a6e3f" : dias <= 30 ? "#856404" : "#a00",
  }),
  empty: { textAlign: "center", padding: "48px", color: "#aaa", fontSize: 14 },
};

// ─── Tooltip custom ───────────────────────────────────────────────────────────
const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background: "#fff", border: "1px solid #f0e6ec", borderRadius: 8, padding: "10px 14px", fontSize: 13 }}>
      <p style={{ fontWeight: 700, color: "#9f0059", marginBottom: 4 }}>{label}</p>
      {payload.map((p, i) => (
        <p key={i} style={{ color: p.color ?? "#333" }}>
          {p.name}: <strong>{typeof p.value === "number" && p.name.toLowerCase().includes("monto")
            ? `$${p.value.toLocaleString("es-AR")}`
            : p.value}</strong>
        </p>
      ))}
    </div>
  );
};

// ─── Componente principal ─────────────────────────────────────────────────────
const OrdenesEntregadas = () => {
  const [ordenes, setOrdenes] = useState([]);
  const [meses, setMeses] = useState(3);
  const [loading, setLoading] = useState(true);

  const traerDatos = (m) => {
    setLoading(true);
    axios.post(`${BASE}/buscar_entregadas.php`, new URLSearchParams({ meses: m }))
      .then(r => { if (Array.isArray(r.data)) setOrdenes(r.data); })
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => { traerDatos(meses); }, [meses]);

  // ── Métricas ───────────────────────────────────────────────────────────────
  // Convierte monto a ARS usando el tipo de cambio guardado en la orden
  const toARS = (o) => {
    const m = parseFloat(o.monto_total) || 0;
    return (o.moneda === "USD" && o.tipo_cambio) ? m * parseFloat(o.tipo_cambio) : m;
  };
  const hayUSD = ordenes.some(o => o.moneda === "USD");
  const totalOrdenes = ordenes.length;
  const montoTotal = ordenes.reduce((s, o) => s + toARS(o), 0);
  const diasPromedio = ordenes.filter(o => o.dias_proceso !== null).length > 0
    ? Math.round(ordenes.filter(o => o.dias_proceso !== null).reduce((s, o) => s + o.dias_proceso, 0) / ordenes.filter(o => o.dias_proceso !== null).length)
    : null;
  const clienteTop = (() => {
    const cnt = {};
    ordenes.forEach(o => { cnt[o.cliente] = (cnt[o.cliente] || 0) + 1; });
    return Object.entries(cnt).sort((a, b) => b[1] - a[1])[0]?.[0] ?? "—";
  })();

  // ── Datos para gráficos ────────────────────────────────────────────────────

  // Órdenes por mes
  const porMes = (() => {
    const map = {};
    ordenes.forEach(o => {
      const m = o.mes;
      if (!map[m]) map[m] = { mes: formatMes(m), ordenes: 0, monto: 0 };
      map[m].ordenes += 1;
      map[m].monto += toARS(o);
    });
    return Object.values(map).sort((a, b) => a.mes > b.mes ? 1 : -1);
  })();

  // Por cliente
  const porCliente = (() => {
    const map = {};
    ordenes.forEach(o => { map[o.cliente] = (map[o.cliente] || 0) + 1; });
    return Object.entries(map).map(([name, value]) => ({ name, value })).sort((a, b) => b.value - a.value).slice(0, 8);
  })();

  // Por proveedor
  const porProveedor = (() => {
    const map = {};
    ordenes.forEach(o => { map[o.proveedor] = (map[o.proveedor] || 0) + 1; });
    return Object.entries(map).map(([name, value]) => ({ name, value })).sort((a, b) => b.value - a.value);
  })();

  return (
    <section style={S.page}>

      {/* Header */}
      <div style={S.header}>
        <div>
          <h2 style={S.title}>Órdenes entregadas</h2>
          <p style={S.subtitle}>Historial y métricas de órdenes completadas</p>
        </div>
        <select style={S.select} value={meses} onChange={e => setMeses(parseInt(e.target.value))}>
          <option value={1}>Último mes</option>
          <option value={3}>Últimos 3 meses</option>
          <option value={6}>Últimos 6 meses</option>
          <option value={12}>Último año</option>
          <option value={999}>Todo el historial</option>
        </select>
      </div>

      {/* KPIs */}
      <div style={S.kpiRow}>
        <div style={S.kpiCard}>
          <p style={S.kpiLabel}>Total órdenes</p>
          <p style={S.kpiValue}>{totalOrdenes}</p>
          <p style={S.kpiSub}>en el período</p>
        </div>
        <div style={{ ...S.kpiCard, borderLeftColor: "#6366F1" }}>
          <p style={S.kpiLabel}>Monto total</p>
          <p style={{ ...S.kpiValue, fontSize: 20 }}>
            ${montoTotal.toLocaleString("es-AR", { minimumFractionDigits: 0 })}
          </p>
          <p style={S.kpiSub}>{hayUSD ? "en ARS (USD al TC del momento)" : "facturado"}</p>
        </div>
        <div style={{ ...S.kpiCard, borderLeftColor: "#22C55E" }}>
          <p style={S.kpiLabel}>Tiempo promedio</p>
          <p style={S.kpiValue}>{diasPromedio !== null ? `${diasPromedio}d` : "—"}</p>
          <p style={S.kpiSub}>desde cotización a entrega</p>
        </div>
        <div style={{ ...S.kpiCard, borderLeftColor: "#F97316" }}>
          <p style={S.kpiLabel}>Cliente top</p>
          <p style={{ ...S.kpiValue, fontSize: 16, marginTop: 6 }}>{clienteTop}</p>
          <p style={S.kpiSub}>mayor cantidad de órdenes</p>
        </div>
      </div>

      {loading ? (
        <p style={{ textAlign: "center", color: "#aaa", padding: 40 }}>Cargando datos...</p>
      ) : ordenes.length === 0 ? (
        <p style={S.empty}>No hay órdenes entregadas en este período.</p>
      ) : (
        <>
          {/* Gráficos fila 1 */}
          <div style={S.chartsRow}>
            <div style={S.chartCard}>
              <p style={S.chartTitle}>Órdenes por mes</p>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={porMes}>
                  <XAxis dataKey="mes" tick={{ fontSize: 11 }} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="ordenes" name="Órdenes" fill="#9f0059" radius={[4,4,0,0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>

            <div style={S.chartCard}>
              <p style={S.chartTitle}>Monto facturado por mes</p>
              <ResponsiveContainer width="100%" height={220}>
                <LineChart data={porMes}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0e6ec" />
                  <XAxis dataKey="mes" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} tickFormatter={v => `$${(v/1000).toFixed(0)}k`} />
                  <Tooltip content={<CustomTooltip />} />
                  <Line type="monotone" dataKey="monto" name="Monto" stroke="#9f0059" strokeWidth={2} dot={{ fill: "#9f0059" }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Gráficos fila 2 */}
          <div style={S.chartsRow}>
            <div style={S.chartCard}>
              <p style={S.chartTitle}>Órdenes por cliente</p>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={porCliente} layout="vertical">
                  <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11 }} />
                  <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={120} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="value" name="Órdenes" radius={[0,4,4,0]}>
                    {porCliente.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>

            <div style={S.chartCard}>
              <p style={S.chartTitle}>Órdenes por proveedor</p>
              <ResponsiveContainer width="100%" height={220}>
                <PieChart>
                  <Pie data={porProveedor} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80} label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`} labelLine={false}>
                    {porProveedor.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Tabla */}
          <div style={S.tableWrap}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  {["Cliente", "Proveedor", "Usuario", "Monto", "Cotización", "Entrega", "Días proceso"].map(h => (
                    <th key={h} style={S.th}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {ordenes.map((o, i) => (
                  <tr key={o.id} style={{ background: i % 2 === 0 ? "#fff" : "#fdf8fb" }}>
                    <td style={{ ...S.td, fontWeight: 600 }}>{o.cliente}</td>
                    <td style={S.td}>{o.proveedor}</td>
                    <td style={S.td}>{o.usuario}</td>
                    <td style={{ ...S.td, fontWeight: 600 }}>
                      {o.monto_total ? (
                        <span>
                          {o.moneda === "USD" ? "USD " : "$"}
                          {parseFloat(o.monto_total).toLocaleString("es-AR")}
                          {o.moneda === "USD" && o.tipo_cambio && (
                            <>
                              {" "}
                              <span style={{ fontSize: 10, fontWeight: 700, padding: "1px 5px",
                                borderRadius: 20, background: "#fffbeb", color: "#92400e",
                                border: "1px solid #fcd34d" }}>USD</span>
                              <br />
                              <span style={{ fontSize: 11, color: "#888", fontWeight: 400 }}>
                                ${(parseFloat(o.monto_total) * parseFloat(o.tipo_cambio)).toLocaleString("es-AR", { minimumFractionDigits: 0 })} ARS
                              </span>
                            </>
                          )}
                        </span>
                      ) : "—"}
                    </td>
                    <td style={S.td}>{formatFecha(o.created_at)}</td>
                    <td style={S.td}>{formatFecha(o.fecha_entrega)}</td>
                    <td style={S.td}>
                      {o.dias_proceso !== null
                        ? <span style={S.diasChip(o.dias_proceso)}>{o.dias_proceso}d</span>
                        : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </section>
  );
};

export default OrdenesEntregadas;
